import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Button, DetailsPageDescription, DetailsPageHeader, Spinner } from '@common/components';
import { ContentParser } from '@common/components/ContentParser';
import { Routes } from '@common/constants';
import { analytics } from '@features/analytics';
import { DenialReason } from '@features/analytics/utilities/analytics.types';
import { TrackGaUpdateBtnFnParams } from '@features/JourneyPicker/JourneyPicker.types';
import { UpgradeItemVersion } from '@features/List/BookableList/UpgradeItem';
import { useGetPackagesAvailability } from '@features/packages/hooks/useGetPackagesAvailability';
import { useRates as useRatesWithPackages } from '@features/rates/queries';
import { useNextAvailableStay } from '@features/rooms';
import { RoomRateConfigurationWizard } from '@features/RoomRateConfigurationWizard';
import { useTranslations } from '@lang';
import { getNumberOfNights } from '@utils/date';
import { some } from '@utils/some';
import { FC, useEffect, useRef, useState } from 'react';
import { useRouter } from 'shared-routing';
import { RoomUpgrade } from 'shared-types/Rate';
import { AvailabilityForm } from './AvailabilityForm';
import { useAvailableRoom, useRateCodeNotice, useRates, useStaticRoom } from './hooks';
import { NextAvailableStay } from './NextAvailableStay';
import { NoMoreRoomsAvailable } from './NoMoreRoomsAvailable';
import { Occupancy } from './Occupancy';
import { Rates } from './RatesListing';
import { Columns, MarkdownWrapper, RoomWrapper, ScrollButtonWrapper } from './Room.styles';
import { RoomProps, RoomReduxProps } from './Room.types';

function getQueryParam(value?: string | string[]): string {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value[0];
  }

  return '';
}

export const Room: FC<RoomProps & RoomReduxProps> = ({
  journeyParams,
  language,
  roomIdsInBasket,
  property,
  changeJourneyParam,
}) => {
  const { t } = useTranslations();
  const { query } = useRouter();
  const isEcom2 = useFeatureIsOn('feature.ecom2');
  const roomId = getQueryParam(query.id);
  const numRoomsOfTypeAlreadyInBasket = roomIdsInBasket?.filter((id) => id === roomId)?.length;
  const { staticRoom, isFetchingStaticRoom } = useStaticRoom(roomId, language);
  const { availableRoom, isFetching: isFetchingAvailableRoom } = useAvailableRoom(
    roomId,
    journeyParams
  );

  const allAvailableRoomsInBasket =
    !!availableRoom?.quantity && numRoomsOfTypeAlreadyInBasket >= availableRoom?.quantity;

  const isQuantityEnough =
    (!!availableRoom?.quantity && availableRoom?.quantity >= journeyParams?.rooms.length) ?? 0;

  const {
    rates,
    lowestRate,
    lowestRateDiscount,
    isFetching: isFetchingRates,
    promoRateCodeIssue,
  } = useRates(roomId, journeyParams, language);

  const fromDate = journeyParams.fromDate.format('YYYY-MM-DD');
  const toDate = journeyParams.toDate
    ? journeyParams.toDate.format('YYYY-MM-DD')
    : journeyParams.fromDate.clone().add(1, 'days').format('YYYY-MM-DD');
  const { rates: ratesWithPackages, isFetching: isFetchingRatesWithPackages } =
    useRatesWithPackages({
      fromDate,
      toDate,
      adults: journeyParams.adults ?? 0,
      children: journeyParams.children ?? 0,
      childAges: journeyParams.childAges,
      rooms: journeyParams.rooms || [
        { roomNumber: 1, adults: journeyParams.adults ?? 0, children: journeyParams.children ?? 0 },
      ],
      promoRateCode: journeyParams.promoRateCode,
    });
  const packagesByRateId = new Map(
    ratesWithPackages.filter((r) => r.package).map((r) => [r.id, r.package])
  );

  const hasPackageData = !isFetchingRatesWithPackages;
  const { data: availability, isFetching: isFetchingAvailability } = useGetPackagesAvailability(
    property,
    rates.map((r) => ({
      id: r.rateId,
      imageUrl: r.rateImageUrl,
      description: r.rateDescription,
      title: r.rateTitle,
      cancellationPolicyText: null,
      unAvailable: !r.available,
      package: packagesByRateId.get(r.rateId) ?? null,
    })),
    journeyParams,
    hasPackageData
  );

  const rateCodeNotice = useRateCodeNotice(journeyParams.promoRateCode, promoRateCodeIssue);

  const scrollToRef = useRef<null | HTMLDivElement>(null);

  const bookingStep = getQueryParam(query.bookingStep);
  const rate = getQueryParam(query.rate);

  const scrollToRate = () => scrollToRef?.current?.scrollIntoView();

  const rateListItems = rates.filter(({ available }, i) => {
    const packageAvailability = availability?.[i];

    return packageAvailability ? !packageAvailability.isUnavailable : available;
  });

  const upgrade = rateListItems.find((thisRate) => thisRate.rateId === rate)?.roomUpgrade;
  const linkParams = { ...query, bookingStep: 'guestDetailsStep' };
  const upgradeLinkParams = { ...linkParams, id: upgrade?.id };
  const allUpgradeRoomsInBasket =
    !!upgrade &&
    !!upgrade.quantity &&
    upgrade.quantity < roomIdsInBasket?.filter((id) => id === upgrade.id)?.length;

  const hasFired = useRef(false);

  if (!isFetchingRates && !some(rateListItems) && !hasFired.current) {
    analytics.track({
      event: 'denial',
      params: {
        reason: DenialReason.NO_AVAILABILITY,
        start_date: journeyParams.fromDate.format('YYYY-MM-DD').toString(),
        end_date: journeyParams.toDate?.format('YYYY-MM-DD').toString() || '',
        items: [
          { item_category: 'rooms', item_id: staticRoom.roomId, item_name: staticRoom.title },
        ],
      },
    });
    hasFired.current = true;
  }

  useEffect(() => {
    if (isFetchingRates) {
      hasFired.current = false;
    }
  }, [isFetchingRates]);

  const isFetchingRoomAvailability =
    isFetchingRates ||
    isFetchingAvailableRoom ||
    isFetchingAvailability ||
    isFetchingRatesWithPackages;

  const hasNoSellableRooms = allAvailableRoomsInBasket || !isQuantityEnough;
  const isRoomUnavailable =
    !isFetchingRoomAvailability && (hasNoSellableRooms || !some(rateListItems));

  const [nextAvailableApplied, setNextAvailableApplied] = useState(0);

  const numberOfNights = getNumberOfNights(journeyParams.fromDate, journeyParams.toDate);

  const { nextStay, isLoading: isLoadingNextStay } = useNextAvailableStay({
    roomCode: roomId,
    fromDate: journeyParams.fromDate,
    nights: numberOfNights,
    adults: journeyParams.adults ?? 0,
    children: journeyParams.children ?? 0,
    isUnavailable: isRoomUnavailable && !allAvailableRoomsInBasket,
  });

  const handleUseNextAvailable = () => {
    if (!nextStay) return;

    changeJourneyParam('fromDate', nextStay.checkIn);
    changeJourneyParam('toDate', nextStay.checkOut);
    changeJourneyParam('spaDate', nextStay.checkIn);
    setNextAvailableApplied((count) => count + 1);
  };

  // Only block initial page load for static room data
  if (isFetchingStaticRoom) {
    return <Spinner showDelayMessage isActive />;
  }

  const isUpgradeStep = bookingStep === 'upgrade' && !!upgrade && !allUpgradeRoomsInBasket;

  const upgradeItem = (
    <UpgradeItemVersion
      trackingItem={{ id: staticRoom.roomId, name: staticRoom.title }}
      isOpen={isUpgradeStep}
      {...(upgrade as RoomUpgrade)}
      linkRoute={Routes.ROOM_BOOKING}
      linkParams={linkParams}
      upgradeLinkParams={upgradeLinkParams}
      language={language}
      numberOfNights={numberOfNights}
      originalRoom={{
        id: staticRoom.roomId,
        name: staticRoom.title,
        imageUrl: staticRoom.images?.[0]?.url,
      }}
    />
  );

  if (isEcom2 && isUpgradeStep) {
    return upgradeItem;
  }

  return (
    <RoomWrapper>
      <DetailsPageHeader
        name={staticRoom.title}
        images={staticRoom.images}
        metadata={{
          price: lowestRate,
          discount: lowestRateDiscount,
          minGuests: journeyParams.adults,
          maxGuests: staticRoom.maxOccupancy,
          displayUrgencyMessaging:
            availableRoom?.displayUrgencyMessaging && rateListItems.length > 0,
          quantity: availableRoom?.quantity,
        }}
      />
      <ScrollButtonWrapper>
        <Button isPrimary onClick={() => scrollToRate()}>
          {t('room.viewRates')}
        </Button>
      </ScrollButtonWrapper>
      <DetailsPageDescription title={t('room.seeRoomDetails')}>
        <Occupancy room={staticRoom} />
        <MarkdownWrapper>
          <ContentParser text={staticRoom.description} escapeHtml={false} />
        </MarkdownWrapper>
      </DetailsPageDescription>
      <DetailsPageDescription title={t('room.amenities')}>
        <Columns>
          {staticRoom.amenities?.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </Columns>
      </DetailsPageDescription>
      <AvailabilityForm
        key={`next-available-${nextAvailableApplied}`}
        roomId={staticRoom.roomId}
        showGuestsPicker={true}
        trackGaUpdate={({ fromDate, toDate, adults, children }: TrackGaUpdateBtnFnParams) => {
          analytics.track({
            event: 'view_item',
            params: {
              adults,
              children,
              start_date: fromDate.format('YYYY-MM-DD').toString(),
              end_date: toDate?.format('YYYY-MM-DD').toString() || '',
              items: [
                { item_category: 'rooms', item_id: staticRoom.roomId, item_name: staticRoom.title },
              ],
            },
          });
        }}
      />
      {isRoomUnavailable && nextStay ? (
        <NextAvailableStay stay={nextStay} onSelectDates={handleUseNextAvailable} />
      ) : !isFetchingRoomAvailability && !isLoadingNextStay && hasNoSellableRooms ? (
        <NoMoreRoomsAvailable />
      ) : (
        <Rates
          rates={rateListItems}
          roomId={roomId}
          journeyParams={journeyParams}
          ref={scrollToRef}
          fetching={isFetchingRoomAvailability || isLoadingNextStay}
          notice={rateCodeNotice}
        />
      )}
      <RoomRateConfigurationWizard
        bookingStep={bookingStep}
        language={language}
        metaEntity={{
          type: 'room',
          id: staticRoom.roomId,
          rates: rateListItems,
        }}
        entity={{ type: 'rate', id: rate ?? '' }}
      />
      {upgradeItem}
    </RoomWrapper>
  );
};
